/**
 * PageMainContent - Wrapper that renders the appropriate main content variant
 *
 * Handles:
 * - Section wrapper with background and padding
 * - Grid layout with configurable column span
 * - Switch between standard MainContent and AttractionMainContent
 *
 * @param colSpan - Number of grid columns to span (use gridSpans from theme)
 * @param headerContent - Optional slot for badges (passed to MainContent for non-attractions)
 */
import { ReactNode } from "react";
import { Section } from "@shared/components/application/layout/Section/Section.web";
import Grid from "@ui-kit/layout/Grid/Grid.web";
import GridItem from "@ui-kit/layout/GridItem/GridItem.web";
import { gridTemplates, gridGaps } from "@shared/theme/props/grid";
import type { PageData } from "../../../adapters/types";
import { MainContent } from "./MainContent.web";
import { AttractionMainContent } from "../../classic/variants/attraction/AttractionMainContent.web";

type ResponsiveColSpan =
  | number
  | Partial<Record<"base" | "sm" | "md" | "lg" | "xl" | "2xl", number>>;

interface PageMainContentProps {
  data: PageData;
  colSpan: ResponsiveColSpan;
  headerContent?: ReactNode;
}

export function PageMainContent({
  data,
  colSpan,
  headerContent,
}: PageMainContentProps): JSX.Element {
  const { isAttraction, attraction } = data;

  const content = isAttraction ? (
    <AttractionMainContent data={data} attraction={attraction} />
  ) : (
    <MainContent data={data} headerContent={headerContent} />
  );

  return (
    <Section
      bg="bg.subtle"
      py={{ base: "md", sm: "xl" }}
      flex={1}
      noPaddingMobile
    >
      <Grid
        h="full"
        width="full"
        gap={gridGaps.default}
        templateColumns={gridTemplates.default}
      >
        <GridItem colSpan={colSpan} height="fit-content">
          {content}
        </GridItem>
      </Grid>
    </Section>
  );
}
